import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { OrderState } from "../../../types";
import { SwftboxOrderForm } from "../../../../utils/types";
import { axiosObj } from "../../../../config/apiClients";

const initialState: OrderState = {
  isLoading: false,
  orders: [],
  order: null,
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false
  }
}

export const getAllOrders = createAsyncThunk(
  "/esouq/order/getAll",
  async ({ paramPage, paramLimit, search, paymentType, metalType, orderStatus }: { paramPage: number; paramLimit: number, search?: string, paymentType: string, metalType: string, orderStatus?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`/esouq/order/getAll?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${paymentType ? `&paymentType=${paymentType}` : ""}${metalType ? `&metalType=${metalType}` : ""}${orderStatus && orderStatus !== "All" ? `&status=${encodeURIComponent(orderStatus)}` : ""}`);

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Orders' });
    }
  }
);

export const getOrderById = createAsyncThunk(
  "/esouq/order/getOrderById",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/order/getById', { orderId });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Orders' });
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "/esouq/order/admin/updateStatus",
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/esouq/order/admin/updateStatus', { _id: id, status });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Orders' });
    }
  }
);

export const createSwftboxOrder = createAsyncThunk(
  "/esouq/order/swftbox/create",
  async (body: SwftboxOrderForm & { orderId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/order/swftbox/create', body);

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to create Swftbox order' });
    }
  }
);

export const getSwftboxTracking = createAsyncThunk(
  "/esouq/order/swftbox/track",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/order/swftbox/track', { orderId });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch Swftbox tracking' });
    }
  }
);

export const getSwftboxLabels = createAsyncThunk(
  "/esouq/order/swftbox/labels",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/order/swftbox/labels', { orderId });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to generate shipping labels' });
    }
  }
);

export const getSwftboxProofOfId = createAsyncThunk(
  "/esouq/order/swftbox/proof-of-id",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/order/swftbox/proof-of-id', { orderId });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch proof of ID' });
    }
  }
);

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    orderStatusUpdate: (state, action) => {
      const orderStatus = action.payload;
      const order = state.orders.find((order) => order._id === orderStatus.id)
      if (order) {
        order.status = orderStatus.status;
      }
    },
    resetOrders(state) {
      state.orders = [];
      state.pagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload.payload.kAllOrders;
        state.pagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      .addCase(getAllOrders.rejected, (state) => {
        state.isLoading = false;
        state.orders = [];
      })
      .addCase(getOrderById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.order = action.payload.payload;
      })
      .addCase(getOrderById.rejected, (state) => {
        state.isLoading = false;
        state.order = null
      })
      .addCase(createSwftboxOrder.fulfilled, (state, action) => {
        const { orderId } = action.meta.arg;
        const { swftboxTracking } = action.payload.payload;
        const listed = state.orders.find((order) => order._id === orderId);
        if (listed) listed.swftboxTracking = swftboxTracking;
        if (state.order?._id === orderId) state.order.swftboxTracking = swftboxTracking;
      });
  },
})

export const { orderStatusUpdate, resetOrders } = ordersSlice.actions;

export default ordersSlice.reducer;